import {
  getCardInstance,
  getCardInstanceDefinition,
  isSetupGameState,
} from "../selectors/card-instances";
import type {
  ActionValidationFailure,
  ActionValidationResult,
  DisbandArmyAction,
  DisbandCommanderAction,
  GameState,
  LaneId,
  PlayerId,
} from "../types";

function invalid(
  code: ActionValidationFailure["code"],
  message: string,
  path: string | null = null,
  details: ActionValidationFailure["details"] = {},
): ActionValidationResult {
  return { valid: false, failure: { code, message, path, details } };
}

function validateDisbandContext(
  state: Readonly<GameState>,
  playerId: PlayerId,
  laneId: LaneId,
): ActionValidationResult {
  if (!isSetupGameState(state)) {
    return invalid(
      "ACTION_NOT_IMPLEMENTED",
      "Voluntary disbanding requires a match with authoritative card-instance ownership.",
      "type",
      { actionKind: "disband-card" },
    );
  }

  if (
    state.phase !== "command" &&
    !(state.schemaVersion === 5 && state.phase === "action-window")
  ) {
    return invalid(
      "WRONG_PHASE",
      "Voluntary disbanding is legal only during the Command Phase.",
      "phase",
      {
        currentPhase: state.phase,
        requiredPhase: state.schemaVersion === 5 ? "action-window" : "command",
      },
    );
  }

  const lane = state.lanes[laneId];
  if (lane.result !== "active") {
    return invalid(
      "TARGET_LANE_NOT_ACTIVE",
      "Cards in a resolved lane cannot be voluntarily disbanded.",
      `lanes.${laneId}.result`,
      { laneId, laneResult: lane.result },
    );
  }

  const side = lane.players[playerId];
  if (side.status !== "active") {
    return invalid(
      "TARGET_LANE_NOT_ACTIVE",
      "Cards can be voluntarily disbanded only from the acting player's active lane side.",
      `lanes.${laneId}.players.${playerId}.status`,
      { laneId, laneSideStatus: side.status },
    );
  }

  if (side.land === null) {
    return invalid(
      "TARGET_LANE_HAS_NO_LAND",
      "Voluntary disbanding requires an active Land in the target lane.",
      `lanes.${laneId}.players.${playerId}.land`,
      { laneId },
    );
  }

  return { valid: true };
}

export function validateDisbandArmyAction(
  state: Readonly<GameState>,
  action: DisbandArmyAction,
): ActionValidationResult {
  const context = validateDisbandContext(state, action.playerId, action.laneId);
  if (!context.valid) return context;
  if (!isSetupGameState(state)) {
    throw new Error("Validated disband context unexpectedly lacks card-instance ownership.");
  }

  const instance = getCardInstance(state, action.armyCardInstanceId);
  if (instance === undefined) {
    return invalid(
      "CARD_INSTANCE_NOT_FOUND",
      "The selected Army card instance does not exist in this match.",
      "armyCardInstanceId",
      { armyCardInstanceId: action.armyCardInstanceId },
    );
  }

  const definition = getCardInstanceDefinition(state, action.armyCardInstanceId);
  if (definition?.type !== "army") {
    return invalid(
      "CARD_NOT_ARMY",
      "Only an Army may be targeted by disband-army.",
      "armyCardInstanceId",
      {
        armyCardInstanceId: action.armyCardInstanceId,
        actualCardType: definition?.type ?? null,
      },
    );
  }

  if (instance.ownerPlayerId !== action.playerId) {
    return invalid(
      "CARD_NOT_OWNED_BY_PLAYER",
      "A player cannot voluntarily disband another player's Army.",
      `cardInstances.${action.armyCardInstanceId}.ownerPlayerId`,
      {
        playerId: action.playerId,
        ownerPlayerId: instance.ownerPlayerId,
        armyCardInstanceId: action.armyCardInstanceId,
      },
    );
  }

  const side = state.lanes[action.laneId].players[action.playerId];
  if (!side.armies.some((army) => army.cardInstanceId === action.armyCardInstanceId)) {
    return invalid(
      "ARMY_NOT_STATIONED_IN_LANE",
      "The selected Army is not stationed in the named lane.",
      `lanes.${action.laneId}.players.${action.playerId}.armies`,
      { laneId: action.laneId, armyCardInstanceId: action.armyCardInstanceId },
    );
  }

  return { valid: true };
}

export function validateDisbandCommanderAction(
  state: Readonly<GameState>,
  action: DisbandCommanderAction,
): ActionValidationResult {
  const context = validateDisbandContext(state, action.playerId, action.laneId);
  if (!context.valid) return context;
  if (!isSetupGameState(state)) {
    throw new Error("Validated disband context unexpectedly lacks card-instance ownership.");
  }

  const instance = getCardInstance(state, action.commanderCardInstanceId);
  if (instance === undefined) {
    return invalid(
      "CARD_INSTANCE_NOT_FOUND",
      "The selected Commander card instance does not exist in this match.",
      "commanderCardInstanceId",
      { commanderCardInstanceId: action.commanderCardInstanceId },
    );
  }

  const definition = getCardInstanceDefinition(state, action.commanderCardInstanceId);
  if (definition?.type !== "commander") {
    return invalid(
      "CARD_NOT_COMMANDER",
      "Only a Commander may be targeted by disband-commander.",
      "commanderCardInstanceId",
      {
        commanderCardInstanceId: action.commanderCardInstanceId,
        actualCardType: definition?.type ?? null,
      },
    );
  }

  if (instance.ownerPlayerId !== action.playerId) {
    return invalid(
      "CARD_NOT_OWNED_BY_PLAYER",
      "A player cannot voluntarily disband another player's Commander.",
      `cardInstances.${action.commanderCardInstanceId}.ownerPlayerId`,
      {
        playerId: action.playerId,
        ownerPlayerId: instance.ownerPlayerId,
        commanderCardInstanceId: action.commanderCardInstanceId,
      },
    );
  }

  const commander = state.lanes[action.laneId].players[action.playerId].commander;
  if (commander?.cardInstanceId !== action.commanderCardInstanceId) {
    return invalid(
      "COMMANDER_NOT_STATIONED_IN_LANE",
      "The selected Commander is not stationed in the named lane.",
      `lanes.${action.laneId}.players.${action.playerId}.commander`,
      { laneId: action.laneId, commanderCardInstanceId: action.commanderCardInstanceId },
    );
  }

  return { valid: true };
}
